import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { combineLatest, Observable, switchMap } from 'rxjs';
import { DOCUMENT } from '@angular/common';

import { CurrentProductService } from '@spartacus/storefront';

import {
  BrandThemesEnum, IReinsurance, PositionEnum, SdComponentWrapperComponent, SdReinsurancesComponent, ThemesEnum
} from '@seb/web-design-system/core';

import {
  CmsLinkDSComponentInterface, ReinsuranceFooterModuleInterface, ReinsuranceModuleInterface,
  WC_CONFIG_PROVIDER, WcConfig
} from '../../../models';

import { LinkMapper, transformHeadlineLevelToTitleLevel } from '../../../mappers';
import { AbstractModuleComponent } from '../abstract-module.component';
import { ImageService, TrackingService } from '../../../services';
import { RepairabilityEnum } from '../../../enums';

@Component({
  selector: 'wc-reinsurance-footer-module',
  templateUrl: './reinsurance-footer-module.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdComponentWrapperComponent,
    SdReinsurancesComponent
  ]
})
export class ReinsuranceFooterModuleComponent extends AbstractModuleComponent<ReinsuranceFooterModuleInterface> implements OnInit {
  reinsurances: IReinsurance[];
  titleLevel: number;
  textConfig: PositionEnum;
  themesConfig: ThemesEnum | BrandThemesEnum;
  isBrand: boolean;
  hasSubline = true;

  constructor(
    private imageService: ImageService,
    private linkMapper: LinkMapper,
    private currentProductService: CurrentProductService,
    private trackingService: TrackingService,
    private cdRef: ChangeDetectorRef,
    @Inject(WC_CONFIG_PROVIDER) protected wcConfig: WcConfig,
    @Inject(DOCUMENT) private document: Document
  ) {
    super();
  }

  override mandatoryComponentFunction = (component: ReinsuranceModuleInterface): boolean => {
    return !!component.headline;
  };

  ngOnInit(): void {
    const currentProductService$ = this.currentProductService.getProduct();

    const cmp$: Observable<ReinsuranceModuleInterface> = this.module$.pipe(
      switchMap((module: ReinsuranceModuleInterface) => {
        this.titleLevel = transformHeadlineLevelToTitleLevel(module.titleLevel);
        this.isBrand = module.isBrand === 'true';
        return this.componentService.getSubComponentsLazyComponentData(
          module,
          'componentsObjects',
          'components',
          this.verifyMandatoryLink
        );
      })
    );

    this.subscriptions.add(
      combineLatest([cmp$, currentProductService$]).subscribe(([cmp, product]) => {
        if (this.wcConfig?.reinssurance?.theme) {
          this.themesConfig = this.wcConfig?.reinssurance?.theme;
        } else {
          this.themesConfig = this.document?.body?.classList.contains('has-header-dark')
            ? ThemesEnum.dark
            : ThemesEnum.default;
        }

        this.textConfig = this.wcConfig?.reinssurance?.textConfig as PositionEnum;
        this.hasSubline = this.wcConfig?.reinssurance?.hasSubline;

        const isReparability = !!product && product.reparabilityIncluded === RepairabilityEnum.YES;

        if (cmp.isRepairabilityComponent === 'true' || cmp.isRepairabilityComponent === true) {
          this.reinsurances = cmp.componentsObjects
            .map((reinsurance, index) =>
              isReparability
                ? this.toReinsurance(reinsurance, product.repairabilityStatus)
                : index !== 1
                  ? this.toReinsurance(reinsurance, product.repairabilityStatus)
                  : undefined
            )
            .filter((obj) => !!obj);
        } else {
          this.reinsurances = cmp.componentsObjects.map((reinsurance) => this.toReinsurance(reinsurance));
        }

        this.setLoaded();
        this.cdRef.markForCheck();
      })
    );
  }

  onClickEvent(event: string): void {
    if (event) {
      this.trackingService.sendEvent('reinsuranceCategoryClick', { categoryName: event });
    }
  }

  verifyMandatoryLink = (link: CmsLinkDSComponentInterface) => (
    !!link.url || !!link.urlLocalized
  );

  private toReinsurance(cmp: ReinsuranceModuleInterface, repairabilityStatus?: string): IReinsurance {
    if (repairabilityStatus) {
      const isRepairability10Y = repairabilityStatus.includes('_10Y_');
      const isRepairability15Y = repairabilityStatus.includes('_15Y_');
      if (isRepairability15Y) {
        cmp.headline = cmp.headline.replace('10', '15');
      } else if (isRepairability10Y) {
        cmp.headline = cmp.headline.replace('15', '10');
      }
    }
    return {
      title: cmp.headline,
      subtitle: cmp.subline,
      media: this.imageService.normalizeMedia(cmp.media),
      icons: cmp.componentsObjects.map((link) => this.linkMapper.toIconLink(link))
    };
  }
}
